import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import tea1 from '../../../assets/christmas25/tea1.webp';
import tea2 from '../../../assets/christmas25/tea2.webp';
import festive from '../../../assets/christmas25/festive.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) { height: 420px; }
  @media (max-width: 768px) { height: 340px; }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  z-index: 1;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  p { margin: 0 0 1rem; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const GalleryImage = styled.div`
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  height: 220px;
`;

const Notice = styled.div.attrs({ className: 'bf-surface' })`
  background: rgba(157, 109, 73, 0.1);
  border: 1px solid rgba(157, 109, 73, 0.3);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 1rem 0 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PriceCard = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  text-align: center;
`;

const PriceType = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const Price = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin-top: 0.25rem;
`;

const CTAButton = styled.button.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  border: none;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;
  cursor: pointer;
  &:hover { opacity: 0.95; transform: translateY(-2px); }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const ModalDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 2rem;
  line-height: 1.6;
`;

const EntryOptions = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const EntryOption = styled.button`
  padding: 1.5rem;
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  background: white;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    font-size: 1rem;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.primary ? `
    background: ${props.theme.colors.accent};
    color: white;
    border: none;

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.primary};
    border: 1px solid ${props.theme.colors.primary};

    &:hover {
      background: ${props.theme.colors.primary};
      color: white;
    }
  `}

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const FestiveAfternoonTea = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const [showBookingModal, setShowBookingModal] = useState(false);

  const meta = {
    title: 'Christmas Afternoon Tea at Belvoir Castle 2025',
    description: 'Indulge in Christmas Afternoon Tea at Belvoir Castle 2025. Festive dining with seasonal savouries, Christmas sweets & elegant tearooms in Leicestershire. Book your Christmas dining experience!',
    canonical: 'https://www.belvoircastle.com/christmas/festive-afternoon-tea'
  };

  const handleBookingClick = () => {
    setShowBookingModal(true);
  };

  const handleEntrySelection = (entryType) => {
    if (entryType === 'castle') {
      // Castle entry goes to new booking journey
      window.location.href = '/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14';
    } else if (entryType === 'garden') {
      // Garden entry goes to old Venpos for gardens-only afternoon tea
      window.location.href = 'https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=40ca044f-534f-4b7f-83ea-b4156f0633d3';
    }
  };

  const handleCloseModal = () => {
    setShowBookingModal(false);
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Christmas Afternoon Tea at Belvoir Castle 2025',
    description: 'Celebrate Christmas with a lavish Festive Afternoon Tea at Belvoir Castle. Enjoy a curated selection of seasonal savouries, delicate Christmas patisserie, and freshly baked scones, served with fine teas in our historic tearooms.',
    image: [
      "https://www.belvoircastle.com/christmas25/tea1.webp",
      "https://www.belvoircastle.com/christmas25/tea2.webp"
    ],
    startDate: '2025-11-29',
    endDate: '2025-12-31',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: 'Aviary Tearoom, Belvoir Castle',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Castle',
        addressLocality: 'Grantham',
        addressRegion: 'Leicestershire',
        postalCode: 'NG32 1PE',
        addressCountry: 'GB'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 52.8931,
        longitude: -0.7792
      }
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
      telephone: '+441476871001',
      email: 'info@belvoircastle.com'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      validFrom: '2025-11-29',
      validThrough: '2025-12-31'
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Adults and families'
    },
    keywords: 'Christmas afternoon tea, festive dining, Christmas dining Leicestershire, afternoon tea castle, Christmas tea, festive food, Belvoir Castle dining',
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Christmas afternoon tea, festive dining Leicestershire, Christmas dining, afternoon tea castle, Christmas tea, festive food, Belvoir Castle dining, Christmas restaurant, Christmas dining experience" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href={meta.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/tea1.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/tea1.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Festive Afternoon Tea at Belvoir Castle" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Christmas Afternoon Tea at Belvoir Castle" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Christmas Afternoon Tea",
                "item": meta.canonical
              }
            ]
          })}
        </script>
      </Helmet>

      <EventHero image={festive}>
        <HeroContent>
          <Title>Festive Afternoon Tea</Title>
          <Strapline>Seasonal indulgence in our elegant tearooms</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <Card>
          <SectionTitle>About the Experience</SectionTitle>
          <Body>
            <p>Celebrate the season with a lavish Festive Afternoon Tea at Belvoir Castle. Enjoy a curated selection of seasonal savouries, delicate patisserie, and freshly baked scones, served with fine teas in the warmth of our historic tearooms.</p>
            <p>Pair your visit with a day exploring the Castle and seasonal displays across the estate. For families, the charming Mice Trail is included with entry tickets.</p>
          </Body>

          <Gallery>
            <GalleryImage src={tea1} />
            <GalleryImage src={tea2} />
          </Gallery>
        </Card>

        <Card>
          <SectionTitle>Key Information</SectionTitle>
          <Notice>Important: Available from 29th November 2025. Select your entry tickets first, then add Festive Afternoon Tea as an extra and choose a time slot at checkout.</Notice>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <div>29 November – 31 December 2025</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <div>Aviary Tearoom, Belvoir Castle</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Seating</InfoLabel>
              <div>Select a time slot during booking</div>
            </InfoItem>

          </InfoGrid>

          <SectionTitle style={{ marginTop: '1.5rem' }}>Prices</SectionTitle>
          <PriceGrid>
            <PriceCard>
              <PriceType>Adult</PriceType>
              <Price>£35</Price>
            </PriceCard>
            <PriceCard>
              <PriceType>Child</PriceType>
              <Price>£15</Price>
            </PriceCard>
          </PriceGrid>
          <Notice>Prices are in addition to your entry ticket. Select entry first, then add Festive Afternoon Tea.</Notice>

          <CTAButton onClick={handleBookingClick}>Start Your Booking</CTAButton>
        </Card>
      </Content>

      <ImageSlant 
        image={festive}
        title="Make a Day of It"
        description="Explore Christmas at Belvoir Castle with magical displays, Santa experiences, and the family-friendly Mice Trail."
        buttonText="Discover Christmas"
        buttonAction={() => window.location.assign('/christmas')}
        imagePosition="right"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>

      {showBookingModal && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
            <ModalTitle>Choose Your Entry Type</ModalTitle>
            <ModalDescription>
              Choose your entry type for the Festive Afternoon Tea experience. Available from 29th November 2025.
            </ModalDescription>

            <EntryOptions>
              <EntryOption
                onClick={() => handleEntrySelection('castle')}
              >
                With Castle Entry - Complete Christmas Experience
                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', fontWeight: 'normal' }}>
                  Full access to the castle, all Christmas displays, Santa experiences, Mice Trail, plus Festive Afternoon Tea
                </div>
              </EntryOption>

              <EntryOption
                onClick={() => handleEntrySelection('garden')}
              >
                With Garden Entry - Gardens & Tea Only
                <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', fontWeight: 'normal' }}>
                  Access to gardens and grounds with Festive Afternoon Tea
                </div>
              </EntryOption>
            </EntryOptions>

            <ModalButtons>
              <ModalButton onClick={handleCloseModal}>
                Cancel
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Page>
  );
};

export default FestiveAfternoonTea;

